import { create } from 'zustand';
import { getUserInfo } from '@/api/auth';
import type { User } from '@/lib/api';
import { authApi } from '@/lib/api';

interface AuthState {
  user: User | null;
  isAuthenticated: boolean;
  accessToken: string | null;
  isInitialized: boolean;
  setAccessToken: (token: string | null) => void;
  setUser: (user: User | null) => void;
  logout: () => void;
  initializeAuth: (force?: boolean) => void;
  updateProfile: (name: string) => void;
  updateGestoriaName: (name: string) => void;
  updateGestoriaLogo: (logoUrl: string | null) => void;
}

export const useAuthStore = create<AuthState>((set, get) => ({
  user: null,
  isAuthenticated: false,
  accessToken: typeof window !== 'undefined' ? localStorage.getItem('access_token') : null,
  isInitialized: false,

  setAccessToken: (token) => {
    if (token) {
      localStorage.setItem('access_token', token);
    } else {
      localStorage.removeItem('access_token');
    }
    set({ accessToken: token, isAuthenticated: !!token });
  },

  setUser: (user) => {
    set({ user, isAuthenticated: !!user });
  },

  logout: () => {
    // Fire-and-forget: notify backend for audit logging
    authApi.logout().catch(() => {});
    localStorage.removeItem('access_token');
    // Keep isInitialized: true - we know the auth state (logged out)
    set({ user: null, accessToken: null, isAuthenticated: false });
  },

  initializeAuth: async (force = false) => {
    // Skip if already initialized, unless forced
    if (get().isInitialized && !force) {
      return;
    }
    
    set({ isInitialized: true });
    
    const token = localStorage.getItem('access_token');
    if (token) {
      try {
        const userInfo = await getUserInfo(token);
        // Only update store if the token hasn't been replaced by another flow
        // (e.g., magic link verification setting a new token concurrently)
        if (localStorage.getItem('access_token') === token) {
          set({ 
            accessToken: token, 
            user: userInfo, 
            isAuthenticated: true,
            isInitialized: true
          });
        }
      } catch {
        // Only clear auth if the token in localStorage is still the one we validated.
        // Prevents a race condition where magic link verification sets a new valid
        // token while this stale getUserInfo call is still in flight.
        if (localStorage.getItem('access_token') === token) {
          localStorage.removeItem('access_token');
          set({ accessToken: null, user: null, isAuthenticated: false, isInitialized: true });
        }
      }
    } else {
      set({ isInitialized: true });
    }
  },
  updateProfile: (name: string) => {
    set((state) => ({
      user: state.user ? { ...state.user, name } : state.user
    }));
  },
  updateGestoriaName: (name: string) => {
    set((state) => ({
      user: state.user
        ? { ...state.user, gestoria: { ...state.user.gestoria, name } }
        : state.user,
    }));
  },
  updateGestoriaLogo: (logoUrl: string | null) => {
    set((state) => ({
      user: state.user
        ? { ...state.user, gestoria: { ...state.user.gestoria, logoUrl } }
        : state.user,
    }));
  },
}));

// Multi-tab logout sync: listen for storage changes
if (typeof window !== 'undefined') {
  window.addEventListener('storage', (event) => {
    if (event.key === 'access_token' && event.newValue === null) {
      // Token was removed in another tab - logout this tab too
      useAuthStore.getState().logout();
    }
  });
}
